import { Badge, Box, Divider, Group, Paper, ScrollArea, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { IconUserCheck } from '@tabler/icons-react'
import { PLAYER_COLOR_HEX } from '@mdev/common'
import type { DetectiveState, GameState, MrxLogEntry } from '@mdev/common'
import { SY, SY_ACCENT_YELLOW } from '../theme'
import { TRANSPORT_COLOR, TRANSPORT_LABEL, TransportIcon } from './TransportIcon'
import { PositionChip } from './PositionChip'
import { formatTicketCount } from '../module'

const TOTAL_ROUNDS = 24
const REVEAL_ROUNDS = new Set( [ 3, 8, 13, 18, 24 ] )

export function GameSidebar( { state }: { state: GameState } ) {
  const awaitingNames = state.detectives.filter( ( d ) => state.awaitingUids.includes( d.uid ) ).map( ( d ) => d.displayName )

  return (
    <Stack
      gap={ 0 }
      h="100%"
      style={ { background: SY.panelBg, borderLeft: `1px solid ${ SY.border }`, width: 360 } }
    >
      <Box
        p="md"
        style={ { borderBottom: `1px solid ${ SY.border }` } }
      >
        <Group
          justify="space-between"
          align="baseline"
        >
          <Title
            order={ 2 }
            c="bright"
          >
            Round { state.round }
          </Title>
          <Text
            c="dimmed"
            size="sm"
          >
            of { TOTAL_ROUNDS }
          </Text>
        </Group>
        <Text
          size="sm"
          c="dimmed"
          mt={ 4 }
        >
          { state.phase === 'mrx' ? "Mr. X is moving" : awaitingNames.length > 0 ? `Waiting on ${ awaitingNames.join( ', ' ) }` : 'Detectives moving' }
        </Text>
      </Box>

      <ScrollArea
        flex={ 1 }
        p="md"
      >
        <Stack gap="md">
          <MrxPanel state={ state } />
          <Divider color={ SY.border } />
          <DetectivesPanel state={ state } />
        </Stack>
      </ScrollArea>
    </Stack>
  )
}

function MrxPanel( { state }: { state: GameState } ) {
  const { mrx } = state
  return (
    <Paper
      p="sm"
      radius="md"
      style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }
    >
      <Group
        justify="space-between"
        align="center"
        mb="xs"
      >
        <Group gap="xs">
          <Text size="xl">{ mrx.avatar }</Text>
          <Stack gap={ 0 }>
            <Text
              c="bright"
              fw={ 600 }
            >
              { mrx.displayName }
            </Text>
            { mrx.lastRevealedStation != null && (
              <Text
                size="xs"
                c="yellow.4"
              >
                Last seen at #{ mrx.lastRevealedStation } (round { mrx.lastRevealedRound })
              </Text>
            ) }
          </Stack>
        </Group>
        <Group gap="xs">
          <Badge
            size="md"
            color="gray"
            variant="light"
            leftSection={ <TransportIcon
              transport="black"
              size={ 14 }
            /> }
          >
            { mrx.blackTickets }
          </Badge>
          <Badge
            size="md"
            color="grape"
            variant="light"
          >
            2× × { mrx.doubleMoves }
          </Badge>
        </Group>
      </Group>

      <Text
        size="xs"
        c="dimmed"
        mb={ 6 }
        tt="uppercase"
        fw={ 600 }
        lts={ 0.5 }
      >
        Transport trail
      </Text>
      <Stack gap={ 4 }>
        { mrx.transportLog.map( ( entry ) => (
          <TrailRow
            key={ entry.round }
            entry={ entry }
          />
        ) ) }
        { Array.from( { length: TOTAL_ROUNDS - mrx.transportLog.length }, ( _, i ) => mrx.transportLog.length + i + 1 ).map( ( round ) => (
          <UpcomingRow
            key={ round }
            round={ round }
          />
        ) ) }
      </Stack>
    </Paper>
  )
}

function TrailRow( { entry }: { entry: MrxLogEntry } ) {
  const isReveal = REVEAL_ROUNDS.has( entry.round )
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      style={ {
        background: isReveal ? SY.revealHighlightBg : 'transparent',
        border: `1px solid ${ isReveal ? SY.revealHighlightBorder : 'transparent' }`,
        borderRadius: 6,
        padding: '4px 8px',
      } }
    >
      <Text
        size="xs"
        c="dimmed"
        w={ 18 }
        ta="right"
      >
        { entry.round }
      </Text>
      <ThemeIcon
        size="sm"
        variant="transparent"
      >
        <TransportIcon
          transport={ entry.transport }
          size={ 18 }
        />
      </ThemeIcon>
      <Text
        size="sm"
        c={ TRANSPORT_COLOR[ entry.transport ] }
        flex={ 1 }
      >
        { TRANSPORT_LABEL[ entry.transport ] }{ entry.doubleMoveSecond ? ' (2×)' : '' }
      </Text>
      { entry.revealedStation != null && (
        <Badge
          size="sm"
          color="yellow"
          variant="filled"
        >
          #{ entry.revealedStation }
        </Badge>
      ) }
    </Group>
  )
}

function UpcomingRow( { round }: { round: number } ) {
  const isReveal = REVEAL_ROUNDS.has( round )
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      style={ { padding: '4px 8px', opacity: 0.4 } }
    >
      <Text
        size="xs"
        c="dimmed"
        w={ 18 }
        ta="right"
      >
        { round }
      </Text>
      <Box w={ 18 } />
      <Text
        size="xs"
        c="dimmed"
        flex={ 1 }
      >
        { isReveal ? '— reveal turn —' : '' }
      </Text>
    </Group>
  )
}

function DetectivesPanel( { state }: { state: GameState } ) {
  return (
    <Stack gap="xs">
      <Text
        size="xs"
        c="dimmed"
        tt="uppercase"
        fw={ 600 }
        lts={ 0.5 }
      >
        Detectives
      </Text>
      { state.detectives.map( ( d ) => (
        <DetectiveCard
          key={ d.uid }
          detective={ d }
          awaiting={ state.awaitingUids.includes( d.uid ) }
        />
      ) ) }
    </Stack>
  )
}

function DetectiveCard( { detective, awaiting }: { detective: DetectiveState; awaiting: boolean } ) {
  const colorHex = PLAYER_COLOR_HEX[ detective.color ]
  return (
    <Paper
      p="sm"
      radius="md"
      style={ {
        background: SY.surfaceBg,
        border: `1px solid ${ awaiting ? SY_ACCENT_YELLOW : SY.border }`,
        boxShadow: awaiting ? '0 0 0 2px rgba(250, 204, 21, 0.15)' : 'none',
      } }
    >
      <Group
        justify="space-between"
        wrap="nowrap"
      >
        <Group
          gap="xs"
          wrap="nowrap"
        >
          <Box
            style={ {
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: colorHex,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0,
            } }
          >
            { detective.avatar }
          </Box>
          <Stack gap={ 0 }>
            <Group gap={ 6 }>
              <Text
                c="bright"
                fw={ 600 }
                size="sm"
              >
                { detective.displayName }
              </Text>
              { awaiting && (
                <ThemeIcon
                  size="xs"
                  color="yellow"
                  variant="light"
                >
                  <IconUserCheck size={ 12 } />
                </ThemeIcon>
              ) }
            </Group>
            { detective.stranded ? (
              <Text
                size="xs"
                c="dimmed"
              >
                Stranded
              </Text>
            ) : (
              <PositionChip
                station={ detective.station }
                size="sm"
              />
            ) }
          </Stack>
        </Group>
        <Group gap={ 4 }>
          <TicketBadge
            transport="taxi"
            count={ detective.tickets.taxi }
          />
          <TicketBadge
            transport="bus"
            count={ detective.tickets.bus }
          />
          <TicketBadge
            transport="underground"
            count={ detective.tickets.underground }
          />
        </Group>
      </Group>
    </Paper>
  )
}

function TicketBadge( { transport, count }: { transport: 'taxi' | 'bus' | 'underground'; count: number } ) {
  return (
    <Stack
      gap={ 0 }
      align="center"
      style={ { minWidth: 32 } }
    >
      <TransportIcon
        transport={ transport }
        size={ 18 }
      />
      <Text
        size="xs"
        c={ count === 0 ? 'red.5' : 'bright' }
        fw={ 600 }
      >
        { formatTicketCount( count ) }
      </Text>
    </Stack>
  )
}

